import { useState, useEffect, useCallback } from 'react';
import { fetchSupplierById, updateSupplier } from '../api/suppliersApi';
import type { SupplierListItem, UpdateSupplierInput } from '../types';

interface UseSupplierOptions {
  supplierId: string | null;
  autoFetch?: boolean;
}

interface UseSupplierReturn {
  supplier: SupplierListItem | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  update: (input: UpdateSupplierInput) => Promise<void>;
  isUpdating: boolean;
}

/**
 * Hook to fetch and manage a single supplier
 */
export function useSupplier({
  supplierId,
  autoFetch = true,
}: UseSupplierOptions): UseSupplierReturn {
  const [supplier, setSupplier] = useState<SupplierListItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSupplier = useCallback(async () => {
    if (!supplierId) {
      setSupplier(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchSupplierById(supplierId);
      setSupplier(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load supplier';
      setError(message);
      console.error('[useSupplier] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [supplierId]);

  const update = useCallback(
    async (input: UpdateSupplierInput) => {
      if (!supplierId) return;

      setIsUpdating(true);
      setError(null);

      try {
        await updateSupplier(supplierId, input);
        // Refetch to get updated view data (includes computed fields)
        await loadSupplier();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update supplier';
        setError(message);
        console.error('[useSupplier] Update error:', err);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    [supplierId, loadSupplier]
  );

  useEffect(() => {
    if (autoFetch && supplierId) {
      loadSupplier();
    }
  }, [autoFetch, supplierId, loadSupplier]);

  return {
    supplier,
    isLoading,
    error,
    refetch: loadSupplier,
    update,
    isUpdating,
  };
}
